<script lang="ts">
    import type {ComponentSize} from '../types';
    import {createEventDispatcher} from 'svelte';
    import {uuid} from 'shared/utils/custom-utils';

    export let id: string = uuid();
    export let checked = false;
    export let indeterminate = false;
    export let size: ComponentSize = 'md';
    export let disabled = false;

    const dispatch = createEventDispatcher<{change: {checked: boolean}}>();

    let inputRef: HTMLInputElement;

    function changeHandler() {
        dispatch('change', {checked: inputRef.checked})
    }

    export function getInputRef(): HTMLInputElement {
        return inputRef;
    }

    function indeterminateState(node: HTMLInputElement, initialIndeterminateValue: boolean) {
        node.indeterminate = initialIndeterminateValue;
        return {
            update: (updatedIndeterminateValue: boolean) => node.indeterminate = updatedIndeterminateValue
        }
    }
</script>


<div class="checkbox-container size-{size}" data-checked={checked}>
    <input {id}
           type="checkbox"
           bind:checked
           {disabled}
           {...$$restProps}
           use:indeterminateState={indeterminate}
           on:change={changeHandler}
           bind:this={inputRef}>

    {#if $$slots.default}
        <!-- tabindex="-1" to make focusout target label instead of body (for form controls touched detection) -->
        <label for={id} tabindex="-1">
            <slot></slot>
        </label>
    {/if}
</div>

<style lang="less" global>
    @import (reference) "bootstrap-less/bootstrap/variables";
    @import (reference) "bootstrap-less/bootstrap/mixins";
    @import (reference) "bootstrap-less/bootstrap/glyphicons";


    @checkbox-size: 1.42857143em; // (normally equals 20px)
    @disabled-opacity: 0.65;
    @default-max-width: 100%;

    .checkbox-custom-focus() {
        outline: none;
        @color: @input-border-focus;
        @color-rgba: rgba(red(@color), green(@color), blue(@color), .6);
        border-color: @color;
        .box-shadow(~"inset 0 1px 1px rgba(0,0,0,.075), 0 0 6px @{color-rgba}");
    }

    .checkbox-container {
        display: inline-block;
        line-height: @checkbox-size;
        max-width: var(--checkbox-max-width, @default-max-width);

        &.size-sm, &.size-xs {
            font-size: 12px;
        }

        &.size-lg {
            font-size: 20px;
        }

        label {
            margin-bottom: 0;
            font-weight: 400;
            cursor: pointer;
            @media screen and (max-width: @screen-sm-min) {
                & {
                    max-width: 90%;
                }
            }
        }

        input[type="checkbox"]:disabled ~ label{
            opacity: @disabled-opacity;
            cursor: default;
        }

        input[type="checkbox"] {
            -webkit-appearance: none;
            appearance: none;
            position: relative;
            display: inline-block;
            line-height: @checkbox-size;
            height: @checkbox-size;
            width: @checkbox-size;
            margin: 0;
            vertical-align: top;
            text-align: center;
            background-color: @input-bg;
            border: 0.0625em solid @input-border;
            border-radius: 0.25em;
            transition: border-color ease-in-out .15s, box-shadow ease-in-out .15s, background-color ease .3s;
            cursor: pointer;

            &:before {
                .glyphicon();

                top: 0;
                width: 100%;
                height: 100%;
                display: flex;
                justify-content: center;
                align-items: center;

            }

            &:focus {
                .checkbox-custom-focus();
            }

            &:checked {
                border-color: @brand-primary;
                background-color: @brand-primary;
                color: @component-active-color;
                .glyphicon-ok();
            }

            &:indeterminate {
                .glyphicon-minus();
            }

            &:disabled {
                opacity: @disabled-opacity;
                cursor: @cursor-disabled;
            }
        }
    }

    .has-error {
        .checkbox-container {
            label {
                color: @state-danger-text;
            }

            input[type="checkbox"] {
                border-color: @state-danger-text;
                box-shadow: 0 0 2px #ce8483;
                .box-shadow(inset 0 1px 1px rgba(0,0,0,.075)); // Redeclare so transitions work
                &:checked {
                    border-color: @state-danger-text;
                }
                &:focus {
                    border-color: darken(@state-danger-text, 10%);
                    @shadow: inset 0 1px 1px rgba(0,0,0,.075), 0 0 6px lighten(@state-danger-text, 20%);
                    .box-shadow(@shadow);
                }
            }
        }

        /* reset error styles if editor is nested in meta-editor which has error */
        .has-not-error {
            .checkbox-container {
                label {
                    color: initial;
                }

                input[type="checkbox"] {
                    border-color: @input-border;
                    box-shadow: none;
                    &:checked {
                        border-color: @brand-primary;
                    }
                    &:focus {
                        .checkbox-custom-focus();
                    }
                }
            }
        }
    }
</style>